import { downloadFlightForOffline, type DownloadInput } from "./download";
import { parseMasterRungs } from "../export/hlsMaster";
import {
  deleteAsset,
  deleteFlight,
  getFlight,
  listAssetsByFlight,
} from "./store";
import { BASEMAPS } from "../export/tileHarvest";
import { OFFLINE_BASEMAP_PROVIDER } from "../map/basemaps";
import { tileRange } from "../export/tiles";
import { fetchTrack, type TrackSample } from "../export/track";
import type {
  Annotations,
  AnnotationsResponse,
  FlightDetailResponse,
  FlightMeta,
  FlightSetDetail,
  PanoramasResponse,
} from "../api/types";

/** Deep-equal on the meta fields that affect cached assets. The plan only
 *  requires comparing `name` + `videoStartUtc`; anything else (frame counts,
 *  panoramas) is handled by the per-asset resumption in downloadFlightForOffline. */
export function metaIsStale(cached: FlightMeta, fresh: FlightMeta): boolean {
  if (cached.name !== fresh.name) return true;
  if (cached.videoStartUtc !== fresh.videoStartUtc) return true;
  return false;
}

/** Drop every asset + the flight record for `flightId` from IndexedDB. */
export async function deleteFlightFromCache(flightId: string): Promise<void> {
  const assets = await listAssetsByFlight(flightId);
  for (const a of assets) await deleteAsset(a.key);
  await deleteFlight(flightId);
}

/** Drop only the basemap tiles of `flightId`. Tile keys carry no provider,
 *  so a provider change has to clear them before the downloader's per-asset
 *  resumption can fetch the new ones; everything else stays and is skipped. */
export async function deleteTilesFromCache(flightId: string): Promise<void> {
  const assets = await listAssetsByFlight(flightId);
  for (const a of assets) if (a.kind === "tile") await deleteAsset(a.key);
}

/** True when the record's tiles were filled by a provider other than the
 *  current one — including records from before the field existed. */
export function basemapsAreStale(cached: { basemapProvider?: string }): boolean {
  return cached.basemapProvider !== OFFLINE_BASEMAP_PROVIDER;
}

export interface RefreshFlightInput {
  flightId: string;
  teamParam: string | undefined;
  fetchDetail: (flightId: string, team: string | undefined) => Promise<FlightDetailResponse>;
  fetchPanoramas: (
    flightId: string,
    team: string | undefined,
  ) => Promise<PanoramasResponse | null>;
  /** Resolve a frame variant URL (typically AnnotationsApi.frameUrl). */
  fetchFramePresign: (
    flightId: string,
    frameId: string,
    variant: "full" | "thumb",
    team: string | undefined,
  ) => Promise<string>;
  fetchAnnotations: (
    flightId: string,
    team: string | undefined,
  ) => Promise<AnnotationsResponse>;
  onUpToDate?: () => void;
  onProgress?: (done: number, total: number, label: string) => void;
  /** Aborts the download (partial assets are kept for resumption). */
  signal?: AbortSignal;
  /** Override for tests — defaults to downloadFlightForOffline. */
  downloadFn?: (input: DownloadInput, opts?: { signal?: AbortSignal }) => Promise<void>;
  /** Override for tests — defaults to fetchTrack. */
  fetchTrackSamples?: (trackUrl: string) => Promise<TrackSample[]>;
  /** Team KMZ overlay listing (Plan 13). Omitted → the flight is cached
   *  without overlays. Both overlay callbacks must be supplied together. */
  fetchOverlayList?: (
    team: string | undefined,
  ) => Promise<Array<{ id: string; name: string }>>;
  /** Fetch one overlay's KMZ bytes; null when it is no longer published. */
  fetchOverlayBytes?: (
    id: string,
    team: string | undefined,
  ) => Promise<ArrayBuffer | null>;
  /** Worker JWT — required to refresh a cached HLS-rung flight. */
  authToken?: string;
  /** Worker origin the JWT is scoped to — required for HLS-rung refresh. */
  workerOrigin?: string;
  /** Authed fetch of a Worker manifest URL as text — required for HLS-rung refresh. */
  fetchManifest?: (url: string) => Promise<string>;
}

/**
 * Refresh a single cached flight: pull the current meta and compare against
 * the cached copy; if anything material changed, evict the per-flight assets
 * and re-run the same orchestrator the initial download used.
 *
 * Both ViewerPage (initial download) and OfflinePage (refresh) call this so
 * the URL-gathering rules (tile bbox, basemap set, frame presign URLs, etc.)
 * stay in one place.
 */
export async function refreshFlight(input: RefreshFlightInput): Promise<void> {
  const cached = await getFlight(input.flightId);
  if (cached === null) {
    throw new Error(`Flight "${input.flightId}" is not cached`);
  }

  const detail = await input.fetchDetail(input.flightId, input.teamParam);
  // A partial record is a stranded fragment left by a cancelled or failed
  // download; its meta may already match the fresh meta, but the assets are
  // incomplete, so always fall through to evict + re-download.
  const stale = cached.partial === true || metaIsStale(cached.meta, detail.flight);
  if (!stale && !basemapsAreStale(cached)) {
    input.onUpToDate?.();
    return;
  }

  if (stale) {
    // Evict the entire per-flight cache so the re-download starts clean.
    // downloadFlightForOffline's per-asset resumption would otherwise keep
    // serving the stale blobs even though the names/IDs changed.
    await deleteFlightFromCache(input.flightId);
  } else {
    // Only the basemap provider changed: clear the tiles and let resumption
    // keep the video, frames and the rest.
    await deleteTilesFromCache(input.flightId);
  }

  const built = await buildDownloadInputFromDetail({
    detail,
    teamParam: input.teamParam,
    videoQuality: cached.videoQuality,
    fetchPanoramas: input.fetchPanoramas,
    fetchFramePresign: input.fetchFramePresign,
    fetchAnnotations: input.fetchAnnotations,
    fetchTrackSamples: input.fetchTrackSamples ?? fetchTrack,
    fetchOverlayList: input.fetchOverlayList,
    fetchOverlayBytes: input.fetchOverlayBytes,
    authToken: input.authToken,
    workerOrigin: input.workerOrigin,
    fetchManifest: input.fetchManifest,
    onProgress: input.onProgress ?? (() => undefined),
  });
  const downloadFn = input.downloadFn ?? downloadFlightForOffline;
  await downloadFn(built, { signal: input.signal });
}

export interface BuildDownloadInputArgs {
  detail: FlightDetailResponse;
  teamParam: string | undefined;
  videoQuality: string;
  fetchPanoramas: (
    flightId: string,
    team: string | undefined,
  ) => Promise<PanoramasResponse | null>;
  fetchFramePresign: (
    flightId: string,
    frameId: string,
    variant: "full" | "thumb",
    team: string | undefined,
  ) => Promise<string>;
  fetchAnnotations: (
    flightId: string,
    team: string | undefined,
  ) => Promise<AnnotationsResponse>;
  fetchTrackSamples: (trackUrl: string) => Promise<TrackSample[]>;
  /** Team KMZ overlay listing (Plan 13). Omitted → `overlays: []`, so the
   *  KMZ-free callers and the export path keep working unchanged. Both
   *  overlay callbacks must be supplied together. */
  fetchOverlayList?: (
    team: string | undefined,
  ) => Promise<Array<{ id: string; name: string }>>;
  /** Fetch one overlay's KMZ bytes; null when it is no longer published. */
  fetchOverlayBytes?: (
    id: string,
    team: string | undefined,
  ) => Promise<ArrayBuffer | null>;
  onProgress: (done: number, total: number, label: string) => void;
  /** Worker JWT — required for HLS rung downloads (the master/variant
   *  playlists are Worker /api routes). */
  authToken?: string;
  /** Worker origin the JWT is scoped to — required for HLS rung downloads. */
  workerOrigin?: string;
  /** Fetch a Worker manifest URL as text (auth attached by the caller).
   *  Required for HLS rung downloads. */
  fetchManifest?: (url: string) => Promise<string>;
}

/**
 * Assemble the DownloadInput from a FlightDetailResponse. Lives here (not on
 * ViewerPage) so the OfflinePage refresh flow uses the same URL-gathering
 * rules as the initial download.
 */
export async function buildDownloadInputFromDetail(
  args: BuildDownloadInputArgs,
): Promise<DownloadInput> {
  const flightId = args.detail.flight.flightId;
  const urls = args.detail.urls;

  // Resolve the video source. Three cases:
  //   - "fmv":     progressive single-file MP4 (urls.video).
  //   - "preview": HLS preview proxy (urls.preview).
  //   - "<rung>":  an HLS ladder rung — reassembled from its variant playlist.
  let videoUrl: string | undefined;
  let videoReassemble: DownloadInput["videoReassemble"];
  let videoBytesEstimate: number | undefined;

  if (args.videoQuality === "fmv") {
    videoUrl = urls.video;
  } else if (args.videoQuality === "preview") {
    videoUrl = urls.preview;
  } else {
    if (args.fetchManifest === undefined || args.authToken === undefined ||
        args.workerOrigin === undefined || urls.manifest === undefined) {
      throw new Error("HLS rung download requires manifest URL + auth context");
    }
    const master = await args.fetchManifest(urls.manifest);
    const rungs = parseMasterRungs(master, urls.manifest);
    const chosen = rungs.find((r) => r.rung === args.videoQuality);
    if (chosen === undefined) {
      throw new Error(`rung "${args.videoQuality}" not found in master playlist`);
    }
    videoReassemble = {
      playlistUrl: chosen.variantUri,
      authToken: args.authToken,
      workerOrigin: args.workerOrigin,
    };
    const dur = args.detail.flight.video.durationSeconds;
    // bandwidth is bits/s -> bytes/s; skip the estimate for a non-positive
    // duration so estimateDownloadBytes falls back to the heuristic rather than
    // a 0 that would always pass the quota gate.
    videoBytesEstimate = dur > 0 ? Math.ceil((chosen.bandwidth / 8) * dur) : undefined;
    // No videoUrl: the rung's :video blob is reassembled from its variant
    // playlist, so neither preview.mp4 nor video.fmv.mp4 is involved.
  }
  if (videoReassemble === undefined && videoUrl === undefined) {
    throw new Error(
      `quality "${args.videoQuality}" not yet supported for offline caching`,
    );
  }

  const durationSeconds = args.detail.flight.video.durationSeconds;
  const PREVIEW_BITRATE_BPS = 300_000; // generator encode.py PREVIEW rung
  const previewBytesEstimate =
    urls.preview !== undefined && durationSeconds > 0
      ? Math.ceil((PREVIEW_BITRATE_BPS / 8) * durationSeconds)
      : undefined;

  const annResp = await args.fetchAnnotations(flightId, args.teamParam);
  const annotations: Annotations = annResp.annotations ?? {
    schemaVersion: 1,
    flightId,
    team: args.detail.flight.team,
    updatedAt: new Date().toISOString(),
    pois: [],
    pins: [],
    frames: [],
  };

  const samples = await args.fetchTrackSamples(urls.track);

  // Plan 13: sibling set-track samples widen the tile bbox so the ghost
  // tracks aren't drawn over blank tiles offline. A sibling whose fetch
  // fails is skipped from BOTH the bbox and the cached set-track list —
  // the same silent per-ghost degrade the online viewer does.
  const setTracks: DownloadInput["setTracks"] = [];
  const sampleSets: TrackSample[][] = [samples];
  for (const member of args.detail.set?.members ?? []) {
    if (member.flightId === flightId || member.trackUrl === "") continue;
    try {
      sampleSets.push(await args.fetchTrackSamples(member.trackUrl));
      setTracks.push({ flightId: member.flightId, url: member.trackUrl });
    } catch {
      // Ghost simply won't be cached; the rest of the download continues.
    }
  }

  // Single pass, never an argument spread: track.json is un-downsampled 24 Hz,
  // so one ~24-minute recording is ~35k samples and a multi-recording day set
  // runs to six figures — `Math.max(...lats)` would blow the call stack
  // (V8 ~130k args, JavaScriptCore ~65k) mid-way through the un-abortable
  // "preparing" phase.
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const set of sampleSets) {
    for (const s of set) {
      if (s.lat < minLat) minLat = s.lat;
      if (s.lat > maxLat) maxLat = s.lat;
      if (s.lon < minLon) minLon = s.lon;
      if (s.lon > maxLon) maxLon = s.lon;
    }
  }
  const padLat = (maxLat - minLat) * 0.1 || 0.01;
  const padLon = (maxLon - minLon) * 0.1 || 0.01;
  const bbox = {
    minLat: minLat - padLat,
    maxLat: maxLat + padLat,
    minLon: minLon - padLon,
    maxLon: maxLon + padLon,
  };
  const tiles: DownloadInput["tiles"] = [];
  for (const basemap of ["map", "satellite"]) {
    const template = BASEMAPS[basemap];
    if (template === undefined) continue;
    for (const t of tileRange(bbox, 11, 16)) {
      const url = template
        .replace("{z}", String(t.z))
        .replace("{x}", String(t.x))
        .replace("{y}", String(t.y));
      tiles.push({ url, basemap, ...t });
    }
  }

  // Panorama ids/count now (for the quota estimate); URLs are re-presigned
  // lazily when the panorama phase starts, since the 1 h TTL can lapse during
  // a long rung download. One re-fetch serves every panorama thunk.
  const panosResp = await args.fetchPanoramas(flightId, args.teamParam);
  let freshPanoUrls: Promise<Map<string, string>> | null = null;
  const lazyPanoUrl = (id: string) => async (): Promise<string> => {
    freshPanoUrls ??= args.fetchPanoramas(flightId, args.teamParam).then(
      (r) => new Map((r?.panoramas ?? []).map((p) => [p.id, p.url])),
    );
    const url = (await freshPanoUrls).get(id);
    if (url === undefined) throw new Error(`panorama "${id}" is no longer listed for this flight`);
    return url;
  };
  const panoramas: DownloadInput["panoramas"] = (panosResp?.panoramas ?? []).map(
    (p) => ({ id: p.id, url: lazyPanoUrl(p.id) }),
  );

  // Frame presigns have a 5-minute TTL — always mint them at fetch time.
  const frames: DownloadInput["frames"] = annotations.frames.map((f) => ({
    id: f.id,
    urls: async () => {
      const [full, thumb] = await Promise.all([
        args.fetchFramePresign(flightId, f.id, "full", args.teamParam),
        args.fetchFramePresign(flightId, f.id, "thumb", args.teamParam),
      ]);
      return { full, thumb };
    },
  }));

  // The cached copy of the set block: sibling track.json URLs are presigned
  // and short-lived, so they are blanked — the offline detail reads the
  // sibling tracks out of IndexedDB instead. Stays `undefined` (never null)
  // when the flight is not in a set, because download.ts JSON-stores it.
  const setInfo: FlightSetDetail | undefined =
    args.detail.set === undefined
      ? undefined
      : {
          ...args.detail.set,
          members: args.detail.set.members.map((m) => ({ ...m, trackUrl: "" })),
        };

  // Overlay KMZs are only collected when the caller wired both callbacks;
  // their bytes stay lazy so a long video phase can't expire them.
  let overlays: DownloadInput["overlays"] = [];
  const { fetchOverlayList, fetchOverlayBytes } = args;
  if (fetchOverlayList !== undefined && fetchOverlayBytes !== undefined) {
    try {
      const list = await fetchOverlayList(args.teamParam);
      overlays = list.map((o) => ({
        id: o.id,
        name: o.name,
        bytes: () => fetchOverlayBytes(o.id, args.teamParam),
      }));
    } catch {
      overlays = []; // overlay listing down — cache the flight without them
    }
  }

  if (urls.meta === undefined || urls.thumb === undefined || urls.track === undefined) {
    throw new Error("flight detail is missing required asset URLs");
  }

  return {
    flightId,
    team: args.detail.flight.team,
    videoQuality: args.videoQuality,
    meta: args.detail.flight,
    assetUrls: {
      video: videoUrl,
      preview: urls.preview,
      track: urls.track,
      meta: urls.meta,
      thumbnail: urls.thumb,
      annotationsBaseline: annotations,
    },
    tiles,
    panoramas,
    frames,
    overlays,
    setInfo,
    setTracks,
    videoReassemble,
    videoBytesEstimate,
    previewBytesEstimate,
    onProgress: args.onProgress,
  };
}
